import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Button, Modal, Row, Col } from 'react-bootstrap'
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from 'react-redux'
import { useAuth } from '../../components/auth/useAuth'
import { getProductStates, addProductState, editProductState } from '../../actions/productStateActions'
import { CustomDataTable } from '../../components/design/Datatables/CustomDataTable'
import Loader from '../../components/loaders/Loader'
import Swal from 'sweetalert2'

export default function ProductStates() {
    const { t } = useTranslation()
    const location = useLocation()
    const dispatch = useDispatch()
    const { clientauth } = useAuth()

    const { states, loading, resultstatus } = useSelector(state => state.productStates)

    const [modalShow, setModalShow] = useState(false)
    const [formState, setFormState] = useState({})
    const [isRegistering, setIsRegistering] = useState(false)
    const [isEditing, setIsEditing] = useState(false)

    const [page] = useState(1)
    const [limit] = useState(25)
    const [search, setSearch] = useState('')

    useEffect(() => {
        if (clientauth) {
            dispatch(getProductStates(page, limit, search))
        }
    }, [dispatch, clientauth, location, page, limit, search])

    const openModal = () => {
        setIsEditing(false)
        setFormState({
            id: '',
            descripcion: '',
            orden: 0,
            active: true
        })
        setModalShow(true)
    }

    const selectProductState = (productState) => {
        setIsEditing(true)
        setFormState(productState)
        setModalShow(true)
    }

    const handleInputChange = ({ target }) => {
        setFormState({
            ...formState,
            [target.name]: target.value
        })
    }

    const handleSubmit = async (e) => {
        e.preventDefault()
        setIsRegistering(true)
        if (isEditing) {
            await dispatch(editProductState(formState._id, formState))
        } else {
            await dispatch(addProductState(formState))
        }
        setIsRegistering(false)
    }

    const closeModal = () => {
        setModalShow(false)
    }

    useEffect(() => {
        if (resultstatus) {
            dispatch({ type: 'PRODUCT_STATE_RESET_RESULT' })
            switch (resultstatus) {
                case 'productStateCreated':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.productStates.addsuccess')
                    }).then(() => {
                        setModalShow(false)
                    })
                    break;
                case 'productStateUpdated':
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.great'),
                        icon: 'success',
                        text: t('admin.productStates.editsuccess')
                    }).then(() => {
                        setModalShow(false)
                    })
                    break;
                default:
                    Swal.fire({
                        showConfirmButton: true,
                        showCancelButton: false,
                        confirmButtonText: t('global.ok'),
                        icon: 'info',
                        text: resultstatus
                    })
                    break;
            }
        }
    }, [resultstatus, dispatch, t])

    const columnsProductStates = [
        {
            name: t('admin.productStates.id'),
            width: '100px',
            minWidth: '100px',
            cell: productState =>
                <div className='cursor' onClick={() => selectProductState(productState)}>
                    {productState.id}
                </div>,
            selector: (productState) => productState.id,
            sortable: true,
        },
        {
            name: t('admin.productStates.description'),
            minWidth: '300px',
            cell: productState =>
                <div className='cursor' onClick={() => selectProductState(productState)}>
                    {productState.descripcion}
                </div>,
            selector: (productState) => productState.descripcion,
            sortable: true,
        },
        {
            name: t('admin.productStates.orden'),
            width: '100px',
            minWidth: '100px',
            cell: productState =>
                <div className='cursor' onClick={() => selectProductState(productState)}>
                    {productState.orden}
                </div>,
            selector: (productState) => productState.orden,
            sortable: true,
        }, {
            name: t('global.actions'),
            width: '100px',
            minWidth: '100px',
            cell: productState =>
                <div>
                    <i className='fas fa-pencil me-2 cursor' onClick={() => selectProductState(productState)}></i>
                </div>,
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
        }
    ]

    const filteredStates = (states || []).filter(productState =>
        productState?.descripcion?.toLowerCase().includes(search.toLowerCase()) ||
        String(productState?.id ?? '').includes(search)
    )

    return (
        <>
            <div className="toolbar">
                <div className="toolbar-left">
                    <div className="input-group" style={{ maxWidth: '320px' }}>
                        <span className="input-group-text"><i className="fas fa-search"></i></span>
                        <input
                            type="text"
                            className="form-control"
                            placeholder={t('admin.productStates.description')}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </div>
                </div>
                <div className="toolbar-right">
                    <Button variant="success" onClick={openModal}>
                        <i className="fas fa-plus"></i>{t('admin.productStates.add')}
                    </Button>
                </div>
            </div>

            {loading ? (
                <Loader />
            ) : (
                <div className="card-simple">
                    <CustomDataTable
                        columns={columnsProductStates}
                        data={filteredStates}
                        pagination={true}
                        exportable={true}
                        printable={true}
                        filter={true}
                    />
                </div>
            )}
            <Modal show={modalShow} onHide={closeModal} size="md" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{isEditing ? t('admin.productStates.edit') : t('admin.productStates.add')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={handleSubmit}>
                        <Row>
                            <Col xs={12} md={6} className="my-2">
                                <label>{t('admin.productStates.id')}</label>
                                <input
                                    className="form-control"
                                    type='number'
                                    name='id'
                                    value={formState.id ?? ''}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Col>
                            <Col xs={12} md={6} className="my-2">
                                <label>{t('admin.productStates.orden')}</label>
                                <input
                                    className="form-control"
                                    type='number'
                                    name='orden'
                                    value={formState.orden ?? 0}
                                    onChange={handleInputChange}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} className="my-2">
                                <label>{t('admin.productStates.description')}</label>
                                <input
                                    className="form-control"
                                    type='text'
                                    name='descripcion'
                                    value={formState.descripcion || ''}
                                    onChange={handleInputChange}
                                    required
                                />
                            </Col>
                        </Row>
                        <Row className="mt-4">
                            <Col className="d-flex justify-content-end align-items-center">
                                <Button variant="secondary" onClick={closeModal} className="me-2">
                                    <i className="fas fa-times me-2"></i>{t('global.cancel')}
                                </Button>
                                <Button variant="primary" type="submit" disabled={isRegistering}>
                                    <i className="fas fa-save me-2"></i>{t('global.save')}
                                </Button>
                            </Col>
                        </Row>
                    </form>
                </Modal.Body>
            </Modal>
        </>
    )
}